import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users,
  Tv,
  QrCode,
  Search,
  Music4,
  Sparkles,
  Radio,
  PlaySquare,
  Smartphone,
  Laptop,
  Zap,
  Globe,
  Github,
  Heart,
  ShieldCheck,
  CheckCircle2,
  ChevronRight,
} from "lucide-react";
import { APP_LOGO_URL, APP_TEXT_LOGO_URL } from "@/constants/branding";

const steps = [
  {
    icon: Tv,
    title: "Host opens OpenKTV on TV",
    description: "Launch OpenKTV on your TV or laptop and create a room instantly.",
  },
  {
    icon: QrCode,
    title: "Guests scan QR code",
    description: "Friends join from their phones by scanning the on-screen QR code.",
  },
  {
    icon: Search,
    title: "Everyone adds songs",
    description: "Search YouTube karaoke tracks and build a shared queue together.",
  },
  {
    icon: Music4,
    title: "Sing together",
    description: "Take turns, keep momentum, and turn any night into karaoke night.",
  },
];

const features = [
  {
    icon: Sparkles,
    title: "No Signup",
    description: "Join instantly with a nickname. No accounts or app installs.",
  },
  {
    icon: Radio,
    title: "Real-Time Sync",
    description: "Guests and host stay in sync with low-latency WebSocket updates.",
  },
  {
    icon: PlaySquare,
    title: "Unlimited Songs",
    description: "Search and queue from YouTube's massive karaoke library.",
  },
  {
    icon: Globe,
    title: "Works Everywhere",
    description: "Built for TV, laptop, tablet, and phone without setup friction.",
  },
  {
    icon: Zap,
    title: "Fast",
    description: "Optimized interactions keep room creation and queue updates snappy.",
  },
  {
    icon: Github,
    title: "Open Source",
    description: "Community-driven and transparent so anyone can build with OpenKTV.",
  },
];

const trustBadges = [
  "Free",
  "No Installation",
  "Mobile Friendly",
  "Powered by YouTube",
];

export default function Landing() {
  return (
    <div className="landing-premium-bg min-h-screen relative overflow-hidden">
      <div className="grain" />
      <div className="absolute inset-0 landing-premium-grid opacity-25 pointer-events-none" />

      <header className="relative z-10 border-b border-white/10 bg-zinc-950/40 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-32 sm:w-40 sm:h-12 flex-shrink-0">
              <img
                src={APP_TEXT_LOGO_URL}
                alt="OpenKTV"
                className="w-full h-full object-contain object-left"
              />
            </div>
          </div>

          <nav className="flex items-center gap-3 text-sm">
            <a
              href="https://github.com"
              className="px-3 py-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
              data-testid="github-link"
            >
              GitHub
            </a>
            <a
              href="https://github.com/sponsors"
              className="px-3 py-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
            >
              Donate
            </a>
            <span className="px-3 py-2 rounded-lg text-zinc-400 border border-white/10 bg-white/[0.02]">
              v1.0
            </span>
          </nav>
        </div>
      </header>

      <main className="relative z-10">
        <section className="max-w-6xl mx-auto px-6 md:px-10 pt-8 md:pt-10 pb-14 md:pb-20">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, ease: "easeOut" }}
            className="grid lg:grid-cols-12 gap-8 md:gap-10 lg:gap-12 items-center"
          >
            <div className="lg:col-span-5 flex justify-center lg:justify-start">
              <div className="relative w-[210px] h-[210px] sm:w-[280px] sm:h-[280px] md:w-[320px] md:h-[320px] lg:w-[360px] lg:h-[360px]">
                <div className="absolute -inset-8 rounded-full bg-[radial-gradient(circle,rgba(168,85,247,0.35),transparent_65%)] blur-2xl" />
                <img
                  src={APP_LOGO_URL}
                  alt="OpenKTV logo"
                  className="relative z-10 w-full h-full object-contain drop-shadow-[0_12px_28px_rgba(0,0,0,0.35)]"
                />
              </div>
            </div>

            <div className="lg:col-span-7 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-purple-400/30 bg-purple-500/10 text-purple-200 text-xs uppercase tracking-[0.2em] mb-6 md:mb-7">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-400 pulse-dot" />
                Live Karaoke, Zero Setup
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold text-white tracking-[-0.03em] leading-[0.95] font-[Outfit]">
                Your Living Room
                <br />
                <span className="bg-gradient-to-r from-purple-200 via-purple-400 to-fuchsia-300 bg-clip-text text-transparent text-glow-purple">
                  Just Became a KTV
                </span>
              </h1>

              <p className="mt-6 md:mt-8 text-lg md:text-xl text-zinc-300 max-w-3xl mx-auto lg:mx-0 leading-relaxed">
                Turn your TV into a collaborative karaoke machine in under 30 seconds.
                Guests scan a QR code, queue YouTube songs, and sing together.
                No app. No signup. Just karaoke.
              </p>

              <div className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center sm:items-stretch">
                <Link
                  to="/host"
                  data-testid="host-room-button"
                  className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl px-8 py-4 text-base font-semibold font-[Outfit] text-white bg-gradient-to-r from-purple-600 to-fuchsia-500 border border-purple-300/20 shadow-[0_12px_34px_-10px_rgba(168,85,247,0.75)] hover:shadow-[0_18px_40px_-8px_rgba(168,85,247,0.85)] hover:-translate-y-0.5 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-300 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
                >
                  Start Hosting
                  <ChevronRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
                </Link>
                <Link
                  to="/join"
                  data-testid="join-room-button"
                  className="w-full sm:w-auto inline-flex items-center justify-center rounded-xl px-8 py-4 text-base font-semibold font-[Outfit] text-zinc-100 bg-zinc-900/55 border border-white/15 hover:border-purple-300/40 hover:bg-zinc-900/80 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-300 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
                >
                  Join a Room
                </Link>
              </div>
            </div>
          </motion.div>
        </section>

        <section className="max-w-6xl mx-auto px-6 md:px-10 pb-20 md:pb-28">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="relative rounded-3xl border border-white/10 bg-zinc-900/35 backdrop-blur-xl p-4 md:p-6 overflow-hidden"
          >
            <div className="absolute -inset-x-24 -top-28 h-56 bg-[radial-gradient(circle,rgba(139,92,246,0.35),transparent_70%)] pointer-events-none" />

            <div className="grid lg:grid-cols-12 gap-4 md:gap-6 relative">
              <div className="lg:col-span-8 rounded-2xl border border-white/10 bg-zinc-950/90 overflow-hidden">
                <div className="h-8 px-3 flex items-center gap-2 border-b border-white/10 bg-zinc-900/80">
                  <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
                  <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
                  <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
                  <span className="ml-2 text-[11px] text-zinc-500 uppercase tracking-[0.16em]">
                    OpenKTV Host Screen
                  </span>
                </div>

                <div className="aspect-[16/9] relative p-5 md:p-7 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(168,85,247,0.18),transparent_45%),radial-gradient(circle_at_80%_70%,rgba(139,92,246,0.16),transparent_50%)]" />

                  <div className="relative h-full rounded-2xl border border-white/10 bg-black/40 p-4 md:p-5 flex flex-col justify-between">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Now Playing</div>
                        <div className="text-white text-lg md:text-2xl font-bold font-[Outfit]">Bohemian Rhapsody</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Room Code</div>
                        <div className="text-xl md:text-2xl font-bold text-white tracking-[0.2em]">ABC123</div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="md:col-span-2 rounded-xl bg-zinc-900/70 border border-white/10 p-3">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Up Next</div>
                        <div className="space-y-2 text-sm">
                          <div className="px-3 py-2 rounded-lg bg-zinc-800/90 text-zinc-100 border border-white/10">Dancing Queen</div>
                          <div className="px-3 py-2 rounded-lg bg-zinc-800/90 text-zinc-100 border border-white/10">My Way</div>
                        </div>
                      </div>
                      <div className="rounded-xl bg-white p-2 border border-white/40 flex items-center justify-center">
                        <div className="w-full h-full rounded-lg border border-zinc-300 bg-[linear-gradient(90deg,#fff_0,#fff_9px,#ececec_10px),linear-gradient(#fff_0,#fff_9px,#ececec_10px)] bg-[size:10px_10px]" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-4 flex flex-col gap-4">
                <div className="rounded-2xl border border-white/10 bg-zinc-950/80 p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-xl bg-purple-500/20 border border-purple-400/30 flex items-center justify-center">
                      <Smartphone size={18} className="text-purple-200" />
                    </div>
                    <div className="text-sm font-semibold text-zinc-100">Guest Phone</div>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-zinc-900/70 p-3 space-y-2">
                    <div className="text-xs text-zinc-400">Scan QR or enter room code</div>
                    <div className="h-9 rounded-lg bg-zinc-800/80 border border-white/10" />
                    <div className="h-9 rounded-lg bg-zinc-800/80 border border-white/10" />
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-zinc-950/80 p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-xl bg-fuchsia-500/20 border border-fuchsia-400/30 flex items-center justify-center">
                      <Laptop size={18} className="text-fuchsia-200" />
                    </div>
                    <div className="text-sm font-semibold text-zinc-100">Queue Control</div>
                  </div>
                  <div className="space-y-2">
                    <div className="px-3 py-2 rounded-lg bg-zinc-900/70 border border-white/10 text-sm text-zinc-200">Search YouTube karaoke songs</div>
                    <div className="px-3 py-2 rounded-lg bg-zinc-900/70 border border-white/10 text-sm text-zinc-200">Add songs in real time</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        <section className="max-w-6xl mx-auto px-6 md:px-10 pb-20 md:pb-28">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.65 }}
            className="mb-10 text-center"
          >
            <div className="text-[11px] uppercase tracking-[0.24em] text-purple-300 mb-3">How it Works</div>
            <h2 className="text-3xl md:text-5xl font-bold font-[Outfit] text-white tracking-tight">
              Karaoke in four simple steps
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {steps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.45, delay: idx * 0.08 }}
                  className="rounded-2xl border border-white/10 bg-zinc-900/45 backdrop-blur p-5 md:p-6 group hover:-translate-y-1 hover:border-purple-300/40 transition-all duration-300"
                >
                  <div className="w-10 h-10 rounded-xl bg-purple-500/15 border border-purple-400/25 flex items-center justify-center mb-4 group-hover:bg-purple-500/25 transition-colors">
                    <Icon size={18} className="text-purple-200" />
                  </div>
                  <h3 className="text-white font-semibold text-lg font-[Outfit] mb-2 leading-tight">{step.title}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-6 md:px-10 pb-20 md:pb-28">
          <div className="grid md:grid-cols-2 gap-5 md:gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5 }}
              className="rounded-3xl p-[1px] bg-gradient-to-br from-purple-400/70 via-fuchsia-400/40 to-transparent"
            >
              <div className="rounded-3xl h-full p-8 md:p-9 bg-zinc-900/55 backdrop-blur-xl border border-white/10 hover:-translate-y-1 transition-transform duration-300">
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 border border-purple-400/25 flex items-center justify-center mb-6">
                  <Tv size={22} className="text-purple-200" />
                </div>
                <h3 className="text-3xl font-bold font-[Outfit] text-white mb-3">Host a Room</h3>
                <p className="text-zinc-300 mb-7 leading-relaxed">Turn your TV into karaoke in seconds.</p>
                <Link
                  to="/host"
                  className="inline-flex items-center justify-center rounded-xl px-6 py-3.5 font-semibold font-[Outfit] text-white bg-gradient-to-r from-purple-600 to-fuchsia-500 border border-purple-300/20 shadow-[0_10px_28px_-10px_rgba(168,85,247,0.75)] hover:-translate-y-0.5 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-300 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
                >
                  Start Hosting
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="rounded-3xl p-[1px] bg-gradient-to-br from-white/30 via-purple-400/30 to-transparent"
            >
              <div className="rounded-3xl h-full p-8 md:p-9 bg-zinc-900/55 backdrop-blur-xl border border-white/10 hover:-translate-y-1 transition-transform duration-300">
                <div className="w-12 h-12 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center mb-6">
                  <Users size={22} className="text-white" />
                </div>
                <h3 className="text-3xl font-bold font-[Outfit] text-white mb-3">Join a Room</h3>
                <p className="text-zinc-300 mb-7 leading-relaxed">Scan the QR code on the TV or enter a room code.</p>
                <Link
                  to="/join"
                  className="inline-flex items-center justify-center rounded-xl px-6 py-3.5 font-semibold font-[Outfit] text-white border border-white/25 bg-zinc-900/50 hover:bg-zinc-900/80 hover:border-purple-300/40 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-300 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
                >
                  Join Room
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-6 md:px-10 pb-20 md:pb-28">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.65 }}
            className="mb-10 text-center"
          >
            <div className="text-[11px] uppercase tracking-[0.24em] text-purple-300 mb-3">Features</div>
            <h2 className="text-3xl md:text-5xl font-bold font-[Outfit] text-white tracking-tight">
              Built for fast, social karaoke nights
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.45, delay: idx * 0.06 }}
                  className="rounded-2xl border border-white/10 bg-zinc-900/45 p-6 hover:-translate-y-1 hover:border-purple-300/35 transition-all duration-300"
                >
                  <div className="w-10 h-10 rounded-xl bg-purple-500/15 border border-purple-400/20 flex items-center justify-center mb-4">
                    <Icon size={18} className="text-purple-200" />
                  </div>
                  <h3 className="text-white font-semibold text-lg font-[Outfit] mb-2">{feature.title}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-6 md:px-10 pb-20 md:pb-28">
          <div className="rounded-2xl border border-white/10 bg-zinc-900/40 p-5 md:p-6 flex flex-wrap items-center justify-center gap-3 md:gap-4">
            {trustBadges.map((badge) => (
              <div
                key={badge}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-white/12 bg-zinc-950/55 text-zinc-200 text-sm"
              >
                <CheckCircle2 size={15} className="text-purple-300" />
                {badge}
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-6 md:px-10 pb-24 md:pb-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="rounded-3xl border border-purple-300/20 bg-gradient-to-br from-purple-500/16 via-zinc-900/75 to-zinc-900/85 p-8 md:p-12 text-center"
          >
            <div className="w-12 h-12 mx-auto rounded-xl bg-purple-500/20 border border-purple-300/30 flex items-center justify-center mb-6">
              <ShieldCheck size={22} className="text-purple-200" />
            </div>
            <h2 className="text-3xl md:text-5xl font-bold font-[Outfit] text-white mb-4 tracking-tight">
              Ready to start singing?
            </h2>
            <p className="text-zinc-300 text-lg md:text-xl max-w-2xl mx-auto mb-8 leading-relaxed">
              Host your first karaoke room in under 30 seconds.
            </p>
            <Link
              to="/host"
              className="inline-flex items-center justify-center rounded-xl px-8 py-4 text-base font-semibold font-[Outfit] text-white bg-gradient-to-r from-purple-600 to-fuchsia-500 border border-purple-300/20 shadow-[0_12px_32px_-10px_rgba(168,85,247,0.8)] hover:-translate-y-0.5 hover:shadow-[0_18px_38px_-10px_rgba(168,85,247,0.85)] transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-300 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
            >
              Start Hosting
            </Link>
          </motion.div>
        </section>
      </main>

      <footer className="relative z-10 border-t border-white/10 bg-zinc-950/45 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-zinc-300">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-fuchsia-500 p-[2px]">
              <img
                src={APP_LOGO_URL}
                alt="OpenKTV logo"
                className="w-full h-full rounded-md object-contain"
              />
            </div>
            <span className="font-semibold font-[Outfit]">OpenKTV</span>
          </div>

          <div className="flex items-center gap-2 text-sm text-zinc-400 flex-wrap justify-center">
            <a href="https://github.com" className="px-3 py-1.5 rounded-lg hover:text-white hover:bg-white/5 transition-colors">GitHub</a>
            <a href="#" className="px-3 py-1.5 rounded-lg hover:text-white hover:bg-white/5 transition-colors">Privacy</a>
            <a href="https://github.com/sponsors" className="px-3 py-1.5 rounded-lg hover:text-white hover:bg-white/5 transition-colors inline-flex items-center gap-1.5">
              <Heart size={14} /> Donate
            </a>
            <span className="px-3 py-1.5 rounded-lg border border-white/10 bg-white/[0.02]">v1.0</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
